import type { Account } from '@adtrackify/at-tracking-event-types';
import * as Sentry from '@sentry/react';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import React from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { protectedRoutes } from './protected-routes';
import { publicRoutes } from './public-routes';
import RouteErrorBoundary from '@/components/error-handling/route-error-boundary';
import { OnboardingSyncProvider } from '@/features/setup/providers/onboarding-sync-provider';
import { AccountInitializer } from '@/app/templates/account-initializer';
import LoggedInWrapper from '@/app/templates/logged-in-wrapper';
import { AppBootSplash } from '@/app/shell/app-boot-splash';
import { Button } from '@/components/ui/button';
import PageLayout from '@/app/shell/page-layout';
import { TooltipProvider } from '@/components/ui/tooltip';
import { PageTracker } from '@/app/shell/page-tracker';
import { resolveIncompleteSignupDestination } from '@/features/signup/helpers/resolve-incomplete-signup-destination';
import { INCOMPLETE_SIGNUP_DESTINATION_REASON } from '@/features/signup/types/resolve-incomplete-signup-destination-enums';
import type { IncompleteSignupDestination } from '@/features/signup/types/resolve-incomplete-signup-destination-types';
import { resolvePostAuthDestination } from '@/lib/navigation/post-auth-destination';
import { CUSTOMER_HOME_PATH } from '@/lib/navigation/constants/post-auth-destination-constants';
import * as Logger from '@/lib/monitoring/logger';
import { findEffectivelyActiveAccount, isAccountEffectivelyActive } from '@/lib/helpers/account/account';
import ErrorPage from '@/app/pages/development-page';
import { AccountScope } from '@/features/workspace/components/account-scope';
import { AccountProvider } from '@/features/workspace/providers/account-provider';
import { useAccountActions, useAccountState } from '@/contexts/account/account-context';
import { AuthProvider } from '@/features/auth/providers/auth-provider';
import { useAuthState } from '@/contexts/auth/auth-context';
import { AUTH_STATE } from '@/contexts/auth/types/auth-context-enums';
import { IntercomProvider } from '@/contexts/intercom/intercom-provider';
import { PaymentMethodsBootstrap } from '@/features/billing/providers/payment-methods-bootstrap';
import { useSignupFlowStore } from '@/features/signup/stores/use-signup-flow-store';
import useStore from '@/store/app/use-store';

const INCOMPLETE_SIGNUP_ALLOWED_PATH_PREFIXES = [
  '/account/settings',
  '/account/stripe-plan',
] as const;

function isIncompleteSignupAllowedPath(pathname: string): boolean {
  return INCOMPLETE_SIGNUP_ALLOWED_PATH_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function AccountsLoadRetry({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4">
      <p className="text-sm text-content-secondary">We couldn&apos;t load your account. Please try again.</p>
      <Button type="button" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

function AuthenticatedBootGate({ children }: { children: React.ReactNode }) {
  const { authState } = useAuthState();
  const { hasLoadedAccounts, accountsLoadFailed, currentAccount } = useAccountState();
  const location = useLocation();
  const isAuthPending = authState === AUTH_STATE.LOADING || authState === AUTH_STATE.RECONNECTING;
  const hasWarmCachedShell = Boolean(currentAccount);
  const shouldHoldColdBootSplash =
    authState === AUTH_STATE.AUTHENTICATED &&
    !hasWarmCachedShell &&
    !hasLoadedAccounts &&
    !accountsLoadFailed;

  if (authState === AUTH_STATE.UNAUTHENTICATED) {
    return (
      <Navigate
        replace
        to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
      />
    );
  }

  if (hasWarmCachedShell && (isAuthPending || authState === AUTH_STATE.AUTHENTICATED)) {
    return <>{children}</>;
  }

  if (isAuthPending) {
    return <AppBootSplash />;
  }

  if (shouldHoldColdBootSplash) {
    return <AppBootSplash />;
  }

  return <>{children}</>;
}

const Dashboard = React.lazy(() => import('@/features/dashboard/overview/dashboard-section'));

// List of routes that shouldn't be filtered out initially (OAuth flow needs these)
// NOTE: Paths must match route definitions in public.tsx (no leading/trailing slashes)
const OAUTH_EXCEPTIONS = [
  'shopify/subscription-complete',
  'shopify/subscription-plans',
  'shopify/login/success',
  'v2/shopify/sign-up',
  'v2/shopify/otp',
  'v2/login',
] as const;

function applyIncompleteSignupSideEffects(
  incompleteDestination: IncompleteSignupDestination,
  userId: string,
  email: string
): void {
  const store = useSignupFlowStore.getState();

  switch (incompleteDestination.reason) {
    case INCOMPLETE_SIGNUP_DESTINATION_REASON.NO_ACCOUNTS:
      if (store.completedStep < 2) {
        store.setCompletedStep(2);
      }
      return;

    case INCOMPLETE_SIGNUP_DESTINATION_REASON.STORED_PROGRESS:
      if (incompleteDestination.storedProgress) {
        store.restoreFromLocalStorage(incompleteDestination.storedProgress);
        if (email) {
          store.setEmail(email);
        }
      }
      return;

    case INCOMPLETE_SIGNUP_DESTINATION_REASON.MULTIPLE_INCOMPLETE_ACCOUNTS:
      // No store changes needed - error is passed via navigate state
      return;

    case INCOMPLETE_SIGNUP_DESTINATION_REASON.CANCELLED_SUBSCRIPTION:
    case INCOMPLETE_SIGNUP_DESTINATION_REASON.RETURN_URL:
    case INCOMPLETE_SIGNUP_DESTINATION_REASON.RESUME_STEP:
      if (userId) {
        store.setUserId(userId);
      }
      return;

    case INCOMPLETE_SIGNUP_DESTINATION_REASON.PLAN_PAYMENT:
    case INCOMPLETE_SIGNUP_DESTINATION_REASON.DEFAULT_PLANS:
      if (userId) {
        store.setUserId(userId);
      }
      if (incompleteDestination.completedStepToSet !== undefined) {
        store.setCompletedStep(incompleteDestination.completedStepToSet);
      }
      return;
  }
}

export function IncompleteSignupGuard({ children }: { children: React.ReactNode }) {
  const { hasLoadedAccounts, accountsList, accountsLoadFailed, currentAccount } = useAccountState();
  const { setCurrentAccount, retryLoadAccounts } = useAccountActions();
  const { user } = useAuthState();
  const storedUser = useStore(s => s.user);
  const location = useLocation();
  // Keyed by from→to so browser back / re-entering home can redirect again.
  // A boolean ref stayed true across protected navigations and left cancelled
  // users stuck on the spinner forever.
  const sideEffectsKeyRef = React.useRef<string | undefined>(undefined);

  const selectedIsActive = isAccountEffectivelyActive(currentAccount);
  const anyActiveAccount = findEffectivelyActiveAccount(accountsList);
  const userId = user?.sub ?? storedUser?.sub ?? '';
  const email = user?.email ?? storedUser?.email ?? '';
  const isOnAllowedPath = isIncompleteSignupAllowedPath(location.pathname);
  const hasWarmCachedShell = Boolean(currentAccount) && !hasLoadedAccounts;

  // Selected account first: another active workspace must not suppress recovery.
  // If selection is unset but some account is active, let AccountInitializer pick —
  // do not treat that as incomplete-signup recovery.
  const needsDestination =
    hasLoadedAccounts &&
    !selectedIsActive &&
    (Boolean(currentAccount) || !anyActiveAccount);
  const signupFlowStore = useSignupFlowStore.getState();
  const incompleteDestination = needsDestination
    ? resolveIncompleteSignupDestination(accountsList, {
        userId,
        completedStep: signupFlowStore.completedStep,
        planId: signupFlowStore.planId ?? undefined,
        selectedAccountId: currentAccount?.id,
      })
    : undefined;

  const isAlreadyAtDestination =
    incompleteDestination !== undefined &&
    location.pathname === incompleteDestination.redirectTo;

  const shouldAllowThrough =
    selectedIsActive ||
    (!currentAccount && Boolean(anyActiveAccount)) ||
    isOnAllowedPath ||
    isAlreadyAtDestination;

  const shouldRedirect =
    incompleteDestination !== undefined && !isOnAllowedPath && !isAlreadyAtDestination;

  const redirectKey =
    shouldRedirect && incompleteDestination
      ? `${location.pathname}->${incompleteDestination.redirectTo}:${incompleteDestination.reason}`
      : undefined;

  React.useEffect(() => {
    if (!shouldRedirect || !incompleteDestination || !redirectKey) {
      return;
    }

    if (sideEffectsKeyRef.current === redirectKey) {
      return;
    }
    sideEffectsKeyRef.current = redirectKey;

    Logger.info('IncompleteSignupGuard: redirecting to signup', {
      reason: incompleteDestination.reason,
      destination: incompleteDestination.redirectTo,
      currentPath: location.pathname,
      accountCount: accountsList.length,
      selectedAccountId: currentAccount?.id,
    });

    // Never overwrite a committed selection with a different account.
    if (
      incompleteDestination.account &&
      (!currentAccount || currentAccount.id === incompleteDestination.account.id)
    ) {
      setCurrentAccount(incompleteDestination.account);
    }

    applyIncompleteSignupSideEffects(incompleteDestination, userId, email);
  }, [
    shouldRedirect,
    redirectKey,
    incompleteDestination,
    accountsList.length,
    currentAccount,
    userId,
    email,
    setCurrentAccount,
    location.pathname,
  ]);

  if (hasWarmCachedShell) {
    return <>{children}</>;
  }

  if (accountsLoadFailed) {
    return <AccountsLoadRetry onRetry={retryLoadAccounts} />;
  }

  if (!hasLoadedAccounts) {
    return <AppBootSplash />;
  }

  if (shouldAllowThrough) {
    return <>{children}</>;
  }

  if (shouldRedirect && incompleteDestination) {
    const navState =
      incompleteDestination.reason === INCOMPLETE_SIGNUP_DESTINATION_REASON.MULTIPLE_INCOMPLETE_ACCOUNTS
        ? { error: incompleteDestination.resumeError }
        : undefined;

    return (
      <Navigate
        replace
        state={navState}
        to={incompleteDestination.redirectTo}
      />
    );
  }

  return <AppBootSplash />;
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <AuthenticatedBootGate>
      <IncompleteSignupGuard>
        <AccountInitializer>
          <PaymentMethodsBootstrap />
          {/* Above the router Outlet on purpose: mounted per-layout it remounted
              on every navigation, and each remount re-projected backend state
              over local writes that had not been persisted yet. */}
          <OnboardingSyncProvider />
          <AccountScope>{children}</AccountScope>
        </AccountInitializer>
      </IncompleteSignupGuard>
    </AuthenticatedBootGate>
  );
}

interface PublicRouteRedirectProps {
  accounts: Account[];
  destination: string;
  activeAccount?: Account;
  incompleteDestination?: IncompleteSignupDestination;
  userId: string;
  email: string;
  preserveCurrentAccount?: boolean;
}

function PublicRouteRedirect({
  accounts,
  destination,
  activeAccount,
  incompleteDestination,
  userId,
  email,
  preserveCurrentAccount = false,
}: PublicRouteRedirectProps) {
  const navigate = useNavigate();
  const { setAccountsList, setCurrentAccount } = useAccountActions();
  const hasRedirectedRef = React.useRef(false);

  React.useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }
    hasRedirectedRef.current = true;

    setAccountsList(accounts);

    if (activeAccount) {
      if (!preserveCurrentAccount) {
        setCurrentAccount(activeAccount);
      }
      void navigate(destination, { replace: true });
      return;
    }

    if (!incompleteDestination) {
      void navigate(destination, { replace: true });
      return;
    }

    if (!preserveCurrentAccount && incompleteDestination.account) {
      setCurrentAccount(incompleteDestination.account);
    }

    // Apply store side-effects using shared helper
    applyIncompleteSignupSideEffects(incompleteDestination, userId, email);

    // Navigate with state for error cases
    const navState = incompleteDestination.reason === INCOMPLETE_SIGNUP_DESTINATION_REASON.MULTIPLE_INCOMPLETE_ACCOUNTS
      ? { error: incompleteDestination.resumeError }
      : undefined;

    void navigate(destination, { replace: true, state: navState });
  }, [
    accounts,
    activeAccount,
    destination,
    email,
    incompleteDestination,
    navigate,
    preserveCurrentAccount,
    setAccountsList,
    setCurrentAccount,
    userId,
  ]);

  return <AppBootSplash />;
}

export function PublicRouteGuard({
  children,
  path,
}: {
  children: React.ReactNode;
  path: string;
}) {
  const { authState, user } = useAuthState();
  const { currentAccount, accountsList, hasLoadedAccounts, accountsLoadFailed } = useAccountState();
  const { retryLoadAccounts } = useAccountActions();
  const [searchParams] = useSearchParams();
  const storedUser = useStore(s => s.user);

  const alwaysBlockedPaths = ['signup', 'signup-existing-user', 'otp'];
  const subscriptionBlockedPaths = ['plans', 'payment', 'payment/verifying'];
  const isLoginPath = path?.includes('login');
  const isAlwaysBlockedPath = alwaysBlockedPaths.some(blockedPath => path === blockedPath);
  const isSubscriptionBlockedPath = subscriptionBlockedPaths.some(blockedPath => path === blockedPath);
  const isBlockedPublicPath = isAlwaysBlockedPath || isLoginPath;
  const isPaymentPath = path === 'payment' || path === 'payment/verifying';

  const needsAccounts =
    authState === AUTH_STATE.AUTHENTICATED && (isBlockedPublicPath || isSubscriptionBlockedPath);

  // Render children optimistically during auth loading and for unauthenticated visitors.
  // Authenticated users will be redirected after auth resolves and accounts load.
  if (authState !== AUTH_STATE.AUTHENTICATED) {
    return children;
  }

  // Provider auto-fetches accounts — fail closed (never allow incomplete auth'd users through)
  if (needsAccounts && accountsLoadFailed) {
    return <AccountsLoadRetry onRetry={retryLoadAccounts} />;
  }
  if (needsAccounts && !hasLoadedAccounts) {
    return <AppBootSplash />;
  }

  const userId = user?.sub ?? storedUser?.sub ?? '';
  const email = user?.email ?? storedUser?.email ?? '';
  const signupFlowStore = useSignupFlowStore.getState();
  const selectedIsActive = isAccountEffectivelyActive(currentAccount);

  // Committed inactive selection on login/signup: route to billing / signup resume.
  // Do NOT apply this to /payment|/plans|/payment/verifying — cancelled resubscribe
  // uses legacy /payment after stripe-plan-confirm, and bouncing to billing mid-flow
  // leaves plan selection + payment UI looking broken.
  if (currentAccount && !selectedIsActive && isBlockedPublicPath) {
    const incompleteDestination = resolveIncompleteSignupDestination(accountsList, {
      userId,
      completedStep: signupFlowStore.completedStep,
      planId: signupFlowStore.planId ?? undefined,
      returnUrl: searchParams.get('returnUrl') ?? undefined,
      selectedAccountId: currentAccount.id,
    });

    return (
      <PublicRouteRedirect
        accounts={accountsList}
        destination={incompleteDestination.redirectTo}
        email={email}
        incompleteDestination={incompleteDestination}
        preserveCurrentAccount
        userId={userId}
      />
    );
  }

  // Never promote another active workspace over a committed inactive selection
  // (e.g. cancelled account on /payment while another account is active).
  const activeAccount = selectedIsActive
    ? currentAccount
    : currentAccount
      ? undefined
      : findEffectivelyActiveAccount(accountsList);
  const isShopifyMigrating = activeAccount?.subscription?.paymentGateway === 'shopify';

  const shouldRedirectSubscriptionPath =
    Boolean(activeAccount) &&
    isSubscriptionBlockedPath &&
    (!isShopifyMigrating || !isPaymentPath);

  if (shouldRedirectSubscriptionPath && activeAccount) {
    return (
      <PublicRouteRedirect
        accounts={accountsList}
        activeAccount={activeAccount}
        destination={CUSTOMER_HOME_PATH}
        email={email}
        preserveCurrentAccount={Boolean(currentAccount)}
        userId={userId}
      />
    );
  }

  if (!isBlockedPublicPath) {
    return children;
  }

  if (activeAccount) {
    return (
      <PublicRouteRedirect
        accounts={accountsList}
        activeAccount={activeAccount}
        destination={resolvePostAuthDestination(searchParams.get('redirect'))}
        email={email}
        preserveCurrentAccount={Boolean(currentAccount)}
        userId={userId}
      />
    );
  }

  const incompleteDestination = resolveIncompleteSignupDestination(accountsList, {
    userId,
    completedStep: signupFlowStore.completedStep,
    planId: signupFlowStore.planId ?? undefined,
    returnUrl: searchParams.get('returnUrl') ?? undefined,
    selectedAccountId: currentAccount?.id,
  });

  return (
    <PublicRouteRedirect
      accounts={accountsList}
      destination={incompleteDestination.redirectTo}
      email={email}
      incompleteDestination={incompleteDestination}
      preserveCurrentAccount={Boolean(currentAccount)}
      userId={userId}
    />
  );
}

type RouteType =
  | false
  | {
      element: React.ReactElement;
      children: Array<{ path: string; element: React.ReactElement }>;
    }
  | {
      path: string;
      element: React.ReactElement;
      children?: undefined;
    };

const isExemptedRoute = (path: string) => OAUTH_EXCEPTIONS.some(item => path === item);

function CatchAllRoute() {
  const { authState } = useAuthState();

  if (authState === AUTH_STATE.LOADING) {
    return <AppBootSplash />;
  }

  return authState === AUTH_STATE.AUTHENTICATED ? <ErrorPage /> : <Navigate replace to="/login" />;
}

const createRouter = (
  isContactAcquisitionFlagEnabled: boolean | undefined,
  isFbIntegrationFlagEnabled: boolean | undefined
) => {
  const processedProtectedRoutes = [
    ...protectedRoutes(isContactAcquisitionFlagEnabled, isFbIntegrationFlagEnabled),
  ]
    .filter((route): route is Exclude<RouteType, false> => Boolean(route))
    .map(route => ({
      ...route,
      element: <ProtectedRoute>{route.element}</ProtectedRoute>,
    }));

  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
  const router = sentryCreateBrowserRouter([
    {
      path: '/',
      element: (
        <AuthProvider>
          <AccountProvider>
            <IntercomProvider>
              <TooltipProvider>
                <PageTracker />
                <Outlet />
              </TooltipProvider>
            </IntercomProvider>
          </AccountProvider>
        </AuthProvider>
      ),
      errorElement: <RouteErrorBoundary />,
      children: [
        ...publicRoutes.map(route => ({
          ...route,
          children: route.children.filter(child => isExemptedRoute(child.path || '')),
        })),

        ...processedProtectedRoutes,

        ...publicRoutes.map(route => {
          const children = [];
          for (const child of route.children) {
            if (isExemptedRoute(child.path || '')) continue;
            children.push({
              ...child,
              element: (
                <PublicRouteGuard path={child.path || ''}>
                  {child.element}
                </PublicRouteGuard>
              ),
            });
          }
          return { ...route, children };
        }),

        {
          element: <LoggedInWrapper />,
          path: '/',
          children: [
            {
              path: '/board/custom-dashboard',
              element: (
                <ProtectedRoute>
                  <PageLayout>
                    <Dashboard />
                  </PageLayout>
                </ProtectedRoute>
              ),
            },
          ],
        },

        {
          path: '*',
          element: <CatchAllRoute />,
        },
      ],
    },
  ]);

  return router;
};

const router = createRouter(true, true);

export function AppRoutes() {
  return (
    <NuqsAdapter>
      <RouterProvider router={router} />
    </NuqsAdapter>
  );
}

export default AppRoutes;
